import '@assets/styles/tiptap-preview.css';

import { Event } from '@src/api/types/event';
import { SnsShareButtons } from '@src/components/SnsShareButtons';
import { RECEPTION_STATUS } from '@src/enum/receptionStatus';
import { useProcessedTableContent } from '@src/hooks/useProcessedContent';
import Button from '@src/ui/elements/button/Button';
import React from 'react';
import { Link } from 'react-router-dom';

interface EventContentProps {
  event: Event;
  showApplicationButton?: boolean;
  showFloatingButton?: boolean;
  shareData: {
    url: string;
    title: string;
    tags: string[];
    disabled: boolean;
  } | null;
}

const EventContent: React.FC<EventContentProps> = ({
  event,
  showApplicationButton = true,
  showFloatingButton = true,
  shareData,
}) => {
  const processedContent = useProcessedTableContent(event?.content || '');

  const handleApplicationClick = (applicationUrl: string) => {
    window.open(applicationUrl, '_blank');
  };

  const getReceptionStatusStyle = (receptionStatus?: number) => {
    switch (receptionStatus) {
      case RECEPTION_STATUS.RECRUITING:
        return 'bg-primary-500 text-white';
      case RECEPTION_STATUS.BEFORE_RECRUITING:
        return 'border border-solid border-primary text-primary';
      case RECEPTION_STATUS.CLOSED:
        return 'bg-gray-200 text-black';
      default:
        return '';
    }
  };

  const getApplicationButtonText = (receptionStatus?: number) => {
    switch (receptionStatus) {
      case RECEPTION_STATUS.RECRUITING:
        return 'お申込みはこちら';
      case RECEPTION_STATUS.BEFORE_RECRUITING:
        return '受付開始前です';
      case RECEPTION_STATUS.CLOSED:
        return '受付は終了しました';
      default:
        return '';
    }
  };

  return (
    <div>
      <div className='lg:mt-10'>
        <img
          className='aspect-[300/168] size-full object-contain'
          src={event?.thumbnail_url}
          alt=''
          width={300}
          height={168}
          loading='lazy'
          data-testid='event-thumbnail'
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '';
          }}
        />
      </div>
      <div className='mt-8 flex items-center justify-between gap-2 md:mt-6'>
        <div
          className={`grid min-h-[26px] flex-[0_0_auto] place-items-center rounded-[4px] ${getReceptionStatusStyle(
            typeof event?.reception_status === 'string'
              ? parseInt(event.reception_status)
              : event?.reception_status,
          )} px-3 text-sm`}
        >
          {event?.reception_status_text}
        </div>
        <div className='border-b-2 border-primary-500 pb-[6px] text-sm text-primary-500'>
          <Link to={`/events?category_slug=${event?.media_category?.slug}`}>
            {event?.media_category?.name}
          </Link>
        </div>
      </div>
      <div className='mt-4 flex flex-wrap gap-x-8 gap-y-3 md:gap-y-2'>
        <div className='flex items-center gap-x-4 md:gap-x-2'>
          <div className='grid min-h-7 place-items-center border border-gray-200 bg-white px-3 text-sm md:shrink-0 md:px-2'>
            開催期間
          </div>
          <div className='grid place-items-center border-gray-200 bg-white font-semibold md:text-[15px]'>
            {event?.event_start_at}
            {event?.event_start_at === event?.event_end_at ? '' : ` ～ ${event?.event_end_at}`}
          </div>
        </div>
        <div className='flex items-center gap-x-4 md:gap-x-2'>
          <div className='grid min-h-7 place-items-center border border-gray-200 bg-white px-3 text-sm md:px-2'>
            募集期間
          </div>
          <div className='grid place-items-center border-gray-200 bg-white text-sm'>
            {event?.recruitment_start_at}
            {event?.recruitment_start_at === event?.recruitment_end_at
              ? ''
              : ` ～ ${event?.recruitment_end_at}`}
          </div>
        </div>
      </div>
      <article className='mt-8'>
        <h1 className='break-all text-[30px] font-semibold leading-normal md:text-[20px]'>
          {event?.title}
        </h1>
        <div className='mt-8 flex flex-wrap gap-3 md:mt-6'>
          {event?.media_tags?.map((tag) => (
            <div
              key={tag.id}
              className='grid min-h-[25px] place-items-center rounded-full border border-gray-200 bg-gray-50 px-4 text-xs'
            >
              <Link to={`/events?tag_slug=${tag.slug}`}>{tag.name}</Link>
            </div>
          ))}
        </div>
        <div className='mt-4 flex justify-end'>
          {shareData && (
            <SnsShareButtons
              url={shareData.url}
              title={shareData.title}
              tags={shareData.tags}
              disabled={shareData.disabled}
              variant='compact'
            />
          )}
        </div>
        <div
          className='tiptap-content mt-10 overflow-hidden break-all'
          dangerouslySetInnerHTML={{
            __html: processedContent,
          }}
        />
        <div className='mt-10'>
          {event?.original_pdf_name && (
            <Link
              className='text-link hover:underline'
              to={event?.pdf_url || '#'}
              target='_blank'
              rel='noopener noreferrer'
            >
              {event?.original_pdf_name}
            </Link>
          )}
        </div>
        <div className='mt-10 flex items-center justify-center gap-x-3 md:mt-12'>
          <div className='grid min-h-[26px] place-items-center border border-gray-200 bg-white px-2 text-sm'>
            募集期間
          </div>
          <div className='grid place-items-center border-gray-200 bg-white text-sm'>
            {event?.recruitment_start_at}
            {event?.recruitment_start_at === event?.recruitment_end_at
              ? ''
              : ` ～ ${event?.recruitment_end_at}`}
          </div>
        </div>
        {showApplicationButton && event?.application_url && (
          <div className='mx-auto mt-4 w-[240px]'>
            <Button
              size='m'
              variant='primary'
              fullRounded
              fullWidth
              disabled={event?.reception_status !== RECEPTION_STATUS.RECRUITING}
              onClick={() => handleApplicationClick(event?.application_url || '')}
            >
              {getApplicationButtonText(
                typeof event?.reception_status === 'string'
                  ? parseInt(event.reception_status)
                  : event?.reception_status,
              )}
            </Button>
          </div>
        )}
      </article>

      {showFloatingButton &&
        event?.application_url &&
        event?.reception_status === RECEPTION_STATUS.RECRUITING && (
          <div className='fixed bottom-[100px] right-[36px] md:bottom-12 md:right-[15px]'>
            <button
              className='grid size-[100px] place-items-center rounded-full border-4 border-white bg-primary-500 shadow-[2px_2px_4px_rgba(0,0,0,.15)]'
              onClick={() => handleApplicationClick(event?.application_url || '')}
            >
              <div className='font-medium text-white'>
                お申込み<br></br>はこちら
              </div>
            </button>
          </div>
        )}
      {shareData && (
        <div className='mt-10 px-6'>
          <SnsShareButtons
            url={shareData.url}
            title={shareData.title}
            tags={shareData.tags}
            disabled={shareData.disabled}
            variant='full'
          />
        </div>
      )}
    </div>
  );
};

export default EventContent;
